<template>
  <div class="ecolabels-content container-fluid p-4 pt-11 pb-12">
    <!-- Page Title -->
    <div class="d-flex flex-column align-items-center mb-5">
      <!-- The white-label portal speaks EKOenergy's own language for this page
           (customer-requested wording); the Soldera app keeps the generic copy. -->
      <h2 class="mb-1">
        {{ t(isEkoBrand ? "ecolabels.ekoenergy.title" : "ecolabels.title") }}
      </h2>
      <p class="text-body-tertiary mb-0">
        {{
          t(
            isEkoBrand
              ? "ecolabels.ekoenergy.description"
              : "ecolabels.description"
          )
        }}
      </p>
    </div>

    <!-- No-access notice: the account has none of the ecolabel gates, so every
         endpoint 403s — point the user at EKOenergy instead of raw errors. -->
    <div v-if="accessDenied" class="card">
      <div class="card-body text-center py-5">
        <font-awesome-icon
          icon="lock"
          size="2x"
          class="text-body-tertiary mb-3"
        />
        <h5>{{ t("ecolabels.no_access_title") }}</h5>
        <i18n-t
          keypath="ecolabels.no_access_contact"
          tag="p"
          class="text-body-tertiary mb-0"
        >
          <a href="mailto:info@ekoenergy.org">info&#64;ekoenergy.org</a>
        </i18n-t>
      </div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <TabsContainer
          :tabs="ecolabelTabs"
          :activeTab="activeTab || 'devices'"
          @change:tab="handleTabChange"
        >
          <template #tab-certificates>
            <EcolabelCertificatesTable
              :statements="statements"
              :loading="statementsLoading"
              :isAdmin="isEkoenergyAdmin"
              @open-upload="openUploadWizard"
              @manage-devices="openManageDevices"
              @refresh="loadStatements"
            />
          </template>

          <template #tab-submissions>
            <SubmissionsTab
              :isAdmin="isEkoenergyAdmin"
              @devices-changed="loadDevices({ background: true })"
            />
          </template>

          <template v-if="isEkoenergyAdmin" #tab-duplicates>
            <DuplicateCleanerTab
              @devices-changed="loadDevices({ background: true })"
            />
          </template>

          <template #tab-devices>
            <!-- Devices Table Section -->
            <section class="mb-3 py-2">
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <h4 class="mb-0 font-serif text-body-emphasis">
                  {{ t("ecolabels.devices_heading") }}
                </h4>
                <div class="d-flex align-items-center gap-2">
                  <MultiSelect
                    v-model="visibleColumns"
                    :options="columnOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="t('ecolabels.select_columns')"
                    class="column-selector"
                    display="chip"
                    :focusOnHover="false"
                  >
                    <template #value="{ value }">
                      <span class="text-body-tertiary">
                        <font-awesome-icon icon="table-columns" class="me-1" />
                        {{
                          t("ecolabels.columns_selected", {
                            count: value?.length ?? 0,
                          })
                        }}
                      </span>
                    </template>
                  </MultiSelect>
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="exportCSV"
                  >
                    <font-awesome-icon icon="download" class="me-1" />
                    {{ t("ecolabels.export_csv") }}
                  </button>
                </div>
              </div>
              <div class="card border-translucent">
                <div class="card-body p-0">
                  <DataTable
                    ref="dt"
                    v-model:selection="selectedDevices"
                    v-model:filters="tableFilters"
                    :value="displayedDevices"
                    dataKey="id"
                    :selectionMode="isEkoenergyAdmin ? 'multiple' : undefined"
                    :metaKeySelection="false"
                    :selectAll="isEkoenergyAdmin ? allFilteredSelected : null"
                    @select-all-change="onSelectAllChange"
                    filterDisplay="row"
                    class="ecolabel-datatable"
                    scrollable
                    scrollHeight="600px"
                    :virtualScrollerOptions="{
                      itemSize: 46,
                      numToleratedItems: 20,
                    }"
                    :loading="loading"
                    sortMode="single"
                    removableSort
                  >
                    <template #empty>
                      <div class="text-center py-4 text-muted">
                        {{ t("ecolabels.no_devices") }}
                      </div>
                    </template>
                    <template #loading>
                      <LoadingIndicatorComponent />
                    </template>

                    <!-- Selection Column -->
                    <Column
                      v-if="isEkoenergyAdmin"
                      selectionMode="multiple"
                      headerStyle="width: 2.5rem"
                      frozen
                    />

                    <!-- Name Column: frozen so the eye keeps its row while scrolling
                         through the optional columns (customer feedback). -->
                    <Column
                      v-if="isColVisible('name')"
                      field="name"
                      :header="t('ecolabels.table.name')"
                      frozen
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('name') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        <div class="fw-semibold" :title="data.name">
                          {{ data.name }}
                        </div>
                        <small v-if="data.plant_ecolabel_id" class="text-muted">
                          {{ data.plant_ecolabel_id }}
                          <span
                            v-if="
                              isEkoenergyAdmin &&
                              data.registration_id_quality &&
                              !['VALID', 'PLAUSIBLE'].includes(
                                data.registration_id_quality
                              )
                            "
                            class="badge bg-secondary ms-1"
                            :title="t('ecolabels.upload.flag_reg_id_hint')"
                          >
                            {{ data.registration_id_quality }}
                          </span>
                        </small>
                      </template>
                      <template #filter>
                        <InputText
                          v-model="searchInput"
                          type="text"
                          class="p-column-filter w-100"
                          :maxlength="MAX_QUERY_LENGTH"
                          :placeholder="
                            t('ecolabels.filters.search_placeholder')
                          "
                        />
                      </template>
                    </Column>

                    <!-- Standard Column (hidden while only one standard exists) -->
                    <Column
                      v-if="isColVisible('standard') && showStandardColumn"
                      field="standard"
                      :header="t('ecolabels.table.standard')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('standard') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        <span :class="getStandardBadgeClass(data.standard)">
                          {{ formatStandard(data.standard) }}
                        </span>
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="standardFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Status Column -->
                    <Column
                      v-if="isColVisible('status')"
                      field="status"
                      :header="t('ecolabels.table.status')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('status') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        <span
                          class="badge rounded-pill"
                          :class="getStatusBadgeClass(data.status)"
                        >
                          {{ formatStatus(data.status) }}
                        </span>
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="statusFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Country Column -->
                    <Column
                      v-if="isColVisible('country')"
                      field="country"
                      :header="t('ecolabels.table.country')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('country') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        <span :title="data.country">{{
                          countryName(data.country)
                        }}</span>
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="countryFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          filter
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Address Column -->
                    <Column
                      v-if="isColVisible('address')"
                      field="address"
                      :header="t('ecolabels.table.address')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('address') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.address ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <DebouncedTextFilter
                          v-model="filterModel.value"
                          :placeholder="t('common.filter')"
                          @update:modelValue="filterCallback()"
                        />
                      </template>
                    </Column>

                    <!-- Renewable Type Column -->
                    <Column
                      v-if="isColVisible('renewable_type')"
                      field="renewable_type"
                      :header="t('ecolabels.table.renewable_type')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('renewable_type') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.renewable_type ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="typeFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Technology Code Column -->
                    <Column
                      v-if="isColVisible('technology_code')"
                      field="technology_code"
                      :header="t('ecolabels.table.technology_code')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('technology_code') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.technology_code ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="technologyCodeFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Capacity Column -->
                    <Column
                      v-if="isColVisible('capacity')"
                      field="capacity"
                      :header="t('ecolabels.table.capacity')"
                      sortable
                      dataType="numeric"
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('capacity') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{
                          data.capacity
                            ? `${data.capacity} ${data.capacity_units}`
                            : "-"
                        }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <DebouncedTextFilter
                          v-model="filterModel.value"
                          :placeholder="
                            t('ecolabels.filters.capacity_placeholder')
                          "
                          @update:modelValue="filterCallback()"
                        />
                      </template>
                    </Column>

                    <!-- COD Column -->
                    <Column
                      v-if="isColVisible('cod')"
                      field="cod"
                      :header="t('ecolabels.table.cod')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('cod') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ formatCod(data.cod) }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="codFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Plant Ecolabel ID Column -->
                    <Column
                      v-if="isColVisible('plant_ecolabel_id')"
                      field="plant_ecolabel_id"
                      :header="t('ecolabels.table.plant_ecolabel_id')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('plant_ecolabel_id'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.plant_ecolabel_id ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <DebouncedTextFilter
                          v-model="filterModel.value"
                          :placeholder="t('common.filter')"
                          @update:modelValue="filterCallback()"
                        />
                      </template>
                    </Column>

                    <!-- Latitude Column -->
                    <Column
                      v-if="isColVisible('latitude')"
                      field="latitude"
                      :header="t('ecolabels.table.latitude')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('latitude') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.latitude ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <DebouncedTextFilter
                          v-model="filterModel.value"
                          :placeholder="t('common.filter')"
                          @update:modelValue="filterCallback()"
                        />
                      </template>
                    </Column>

                    <!-- Longitude Column -->
                    <Column
                      v-if="isColVisible('longitude')"
                      field="longitude"
                      :header="t('ecolabels.table.longitude')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('longitude') }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.longitude ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <DebouncedTextFilter
                          v-model="filterModel.value"
                          :placeholder="t('common.filter')"
                          @update:modelValue="filterCallback()"
                        />
                      </template>
                    </Column>

                    <!-- Date Reviewed Column -->
                    <Column
                      v-if="isColVisible('date_reviewed')"
                      field="date_reviewed"
                      :header="t('ecolabels.table.date_reviewed')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('date_reviewed'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ formatCod(data.date_reviewed) }}
                      </template>
                    </Column>

                    <!-- Valid From Column -->
                    <Column
                      v-if="isColVisible('valid_from')"
                      field="valid_from"
                      :header="t('ecolabels.table.valid_from')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('valid_from'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ formatCod(data.valid_from) }}
                      </template>
                    </Column>

                    <!-- Valid Through Column -->
                    <Column
                      v-if="isColVisible('valid_through')"
                      field="valid_through"
                      :header="t('ecolabels.table.valid_through')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('valid_through'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ formatCod(data.valid_through) }}
                      </template>
                    </Column>

                    <!-- Public Notes Column -->
                    <Column
                      v-if="isColVisible('public_notes')"
                      field="public_notes"
                      :header="t('ecolabels.table.public_notes')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('public_notes'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.public_notes ?? "-" }}
                      </template>
                    </Column>

                    <!-- Relevant Link Column -->
                    <Column
                      v-if="isColVisible('relevant_link')"
                      field="relevant_link"
                      :header="t('ecolabels.table.relevant_link')"
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('relevant_link'),
                      }"
                    >
                      <template #body="{ data }">
                        <a
                          v-if="
                            data.relevant_link && isSafeUrl(data.relevant_link)
                          "
                          :href="data.relevant_link"
                          target="_blank"
                          rel="noopener noreferrer"
                          @click.stop
                        >
                          <font-awesome-icon icon="external-link-alt" />
                          {{ t("ecolabels.table.link") }}
                        </a>
                        <span v-else class="text-muted">-</span>
                      </template>
                    </Column>

                    <!-- Registration Authority Column -->
                    <Column
                      v-if="isColVisible('registration_authority')"
                      field="registration_authority"
                      :header="t('ecolabels.table.registration_authority')"
                      sortable
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{
                        width: getColWidth('registration_authority'),
                      }"
                    >
                      <template #sorticon="{ sortOrder }">
                        <font-awesome-icon
                          :icon="getSortIcon(sortOrder)"
                          class="pv-datatable-sort-icon"
                        />
                      </template>
                      <template #body="{ data }">
                        {{ data.registration_authority ?? "-" }}
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <MultiSelect
                          v-model="filterModel.value"
                          :options="registryFilterOptions"
                          optionLabel="label"
                          optionValue="value"
                          :placeholder="t('common.filter')"
                          class="p-column-filter w-100"
                          display="chip"
                          appendTo="body"
                          :focusOnHover="false"
                          @change="filterCallback()"
                        >
                          <template #value="{ value, placeholder }">
                            <span
                              v-if="value && value.length"
                              class="text-primary fw-bold"
                            >
                              {{ t("common.filter") }}
                            </span>
                            <span v-else>{{ placeholder }}</span>
                          </template>
                        </MultiSelect>
                      </template>
                    </Column>

                    <!-- Documents Column -->
                    <Column
                      v-if="isColVisible('documents')"
                      field="document_count"
                      :header="t('ecolabels.table.documents')"
                      :showFilterMatchModes="false"
                      :showFilterMenu="false"
                      :headerStyle="{ width: getColWidth('documents') }"
                    >
                      <template #body="{ data }">
                        <button
                          v-if="data.document_count > 0"
                          class="badge bg-secondary border-0"
                          :title="t('ecolabels.documents_modal.open_hint')"
                          @click.stop="openDocumentsModal(data)"
                        >
                          <font-awesome-icon icon="paperclip" class="me-1" />
                          {{ data.document_count }}
                        </button>
                        <span v-else class="text-muted">-</span>
                      </template>
                    </Column>

                    <!-- Actions Column (always visible for admins, not in column selector) -->
                    <Column
                      v-if="isEkoenergyAdmin"
                      :header="t('ecolabels.table.actions')"
                      headerStyle="width: 6rem"
                      :exportable="false"
                      frozen
                      alignFrozen="right"
                    >
                      <template #body="{ data }">
                        <div class="btn-group btn-group-sm">
                          <button
                            class="btn btn-outline-primary"
                            :title="t('common.edit')"
                            @click.stop="openEditModal(data)"
                          >
                            <font-awesome-icon icon="edit" />
                          </button>
                          <button
                            class="btn btn-outline-danger"
                            :title="t('common.delete')"
                            @click.stop="confirmDelete(data)"
                          >
                            <font-awesome-icon icon="trash" />
                          </button>
                        </div>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>

              <!-- Result count: total catalog size, or "showing X of Y" under filters -->
              <div class="text-body-tertiary small mt-2 text-end">
                {{
                  anyFilterActive
                    ? t("ecolabels.showing_count", {
                        filtered: filteredDevices.length,
                        total: devices.length,
                      })
                    : t("ecolabels.total_count", { total: devices.length })
                }}
              </div>
            </section>

            <!-- Action Bar (shown when items selected) -->
            <div
              v-if="isEkoenergyAdmin && selectedDevices.length > 0"
              class="d-flex justify-content-between align-items-center mt-3 p-3 bg-light border rounded"
            >
              <span class="text-muted">
                {{ selectedDevices.length }}
                {{ t("ecolabels.devices_selected") }}
              </span>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-danger btn-sm"
                  @click="confirmBulkDelete"
                >
                  <font-awesome-icon icon="trash" class="me-1" />
                  {{ t("ecolabels.bulk_delete") }}
                </button>
              </div>
            </div>
          </template>
        </TabsContainer>
      </div>
    </div>

    <!-- Edit Device Modal -->
    <EcolabelDeviceModal
      v-if="showEditModal"
      :device="editingDevice"
      :standards="standardFilterOptions"
      :statuses="statusFilterOptions"
      :companyId="selectedCompanyId"
      @close="closeEditModal"
      @saved="onDeviceSaved"
    />

    <!-- Device Documents Modal -->
    <template v-if="documentsModalDevice">
      <div
        class="modal fade show d-block"
        tabindex="-1"
        @click.self="documentsModalDevice = null"
      >
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title text-truncate">
                {{
                  t("ecolabels.documents_modal.title", {
                    name: documentsModalDevice.name,
                  })
                }}
              </h5>
              <button
                type="button"
                class="btn-close"
                @click="documentsModalDevice = null"
              />
            </div>
            <div class="modal-body">
              <LoadingIndicatorComponent v-if="documentsLoading" />
              <div v-else-if="!deviceDocuments.length" class="text-muted py-2">
                {{ t("ecolabels.documents_modal.empty") }}
              </div>
              <ul v-else class="list-group list-group-flush">
                <li
                  v-for="doc in deviceDocuments"
                  :key="doc.id"
                  class="list-group-item d-flex justify-content-between align-items-center px-0"
                >
                  <div class="text-truncate me-2">
                    <div class="text-truncate">{{ doc.file_name }}</div>
                    <small class="text-muted">
                      {{ formatDate(doc.created_at.slice(0, 10)) }}
                    </small>
                  </div>
                  <button
                    class="btn btn-outline-secondary btn-sm text-nowrap"
                    @click="downloadDocument(doc)"
                  >
                    <font-awesome-icon icon="download" class="me-1" />
                    {{ t("ecolabels.documents_modal.download") }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" />
    </template>

    <!-- Upload Cancellation Statement Wizard -->
    <EcolabelUploadWizardModal
      v-if="showUploadWizard"
      :companyId="selectedCompanyId"
      :allDevices="rawDevices"
      :isAdmin="isEkoenergyAdmin"
      @close="closeUploadWizard"
      @uploaded="onStatementUploaded"
    />

    <!-- Manage Devices Modal -->
    <EcolabelManageDevicesModal
      v-if="showManageDevices && managingStatement"
      :statement="managingStatement"
      :allDevices="rawDevices"
      :isAdmin="isEkoenergyAdmin"
      @close="closeManageDevices"
      @saved="onManageDevicesSaved"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useAccount } from "@/composables/account/useAccount";
import { formatDate } from "@/composables/formatUtils";
import { useAccountFeatureFlags } from "@/composables/useAccountFeatureFlags";
import { getBranding } from "@/branding";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { Tab, useTabs } from "@/composables/useTabs";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { isSafeUrl } from "@/utils/urlUtils";
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import { refDebounced, useLocalStorage } from "@vueuse/core";
import { differenceBy, unionBy } from "lodash-es";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ecolabelsApi } from "./api/ecolabelsApi";
import type {
  CancellationStatement,
  EcolabelDevice,
  FilterOption,
} from "./api/ecolabelsTypes";
import EcolabelCertificatesTable from "./components/EcolabelCertificatesTable.vue";
import EcolabelDeviceModal from "./components/EcolabelDeviceModal.vue";
import EcolabelManageDevicesModal from "./components/EcolabelManageDevicesModal.vue";
import EcolabelUploadWizardModal from "./components/EcolabelUploadWizardModal.vue";
import DebouncedTextFilter from "./DebouncedTextFilter.vue";
import {
  MAX_QUERY_LENGTH,
  clampQuery,
  searchEcolabelDevices,
} from "./deviceSearch";
import DuplicateCleanerTab from "./duplicates/DuplicateCleanerTab.vue";
import SubmissionsTab from "./submissions/SubmissionsTab.vue";
import {
  ALL_ECOLABEL_COLUMNS,
  DEFAULT_ECOLABEL_COLUMNS,
  type EcolabelColumnKey,
} from "./ecolabelTableColumns";
import { getSortIcon } from "@/utils/sortIcons";

const { t } = useI18n();

const isEkoBrand = getBranding().key === "ekoenergy";
const route = useRoute();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const { accountData } = useAccount();
const { hasFlag: hasAccountFlag } = useAccountFeatureFlags(accountData);

const isEkoenergyAdmin = hasAccountFlag("ekoenergy_admin_enabled");

const selectedCompanyId = computed<number>(() => {
  const companies = accountData.companies ?? [];
  return companies.length > 0 ? (companies[0].id ?? 0) : 0;
});

const ecolabelTabs = computed<Tab[]>(() => [
  {
    id: "devices",
    name: t("ecolabels.devices_heading"),
    icon: "solar-panel",
  },
  {
    id: "submissions",
    name: t("ecolabels.submissions.title"),
    icon: "file-arrow-up",
  },
  ...(isEkoenergyAdmin.value
    ? [
        {
          id: "duplicates",
          name: t("ecolabels.duplicates.title"),
          icon: "clone",
        },
      ]
    : []),
  // WORKAROUND: the EKOenergy Certificates tab (id "certificates") stays hidden until
  // the certificate-issuing flow is agreed with EKOenergy (per the V1 feedback call).
  // The feature itself is intact — the #tab-certificates template and its backing code
  // remain; adding the tab entry back to this list restores it.
]);

const { activeTab, setTab: handleTabChange } = useTabs("ecolabels", {
  tabs: ecolabelTabs,
  defaultTabId: "devices",
});

// Data
const dt = useTemplateRef<{ $el?: HTMLElement }>("dt");
const loading = ref(false);
// The catalog is gated (ekoenergy_authorized_seller / ecolabels_enabled / admin). A
// logged-in account without any of those gets 403s from every ecolabels endpoint —
// render a "contact EKOenergy" notice instead of surfacing raw errors.
const accessDenied = ref(false);

const handleLoadError = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403) {
    accessDenied.value = true;
    return;
  }
  globalErrorHandler(error);
};
const devices = ref<(EcolabelDevice & { document_count: number })[]>([]);
const rawDevices = ref<EcolabelDevice[]>([]);
const selectedDevices = ref<EcolabelDevice[]>([]);

const allFilteredSelected = computed(() => {
  const rows = filteredDevices.value;
  if (rows.length === 0 || selectedDevices.value.length < rows.length)
    return false;
  const selectedIds = new Set(selectedDevices.value.map((d) => d.id));
  return rows.every((row) => selectedIds.has(row.id));
});

// Select-all only touches the rows currently passing the filter: checking adds
// them to the selection, unchecking removes them — selections made under other
// filters survive.
const onSelectAllChange = (event: { checked: boolean }) => {
  selectedDevices.value = event.checked
    ? unionBy(selectedDevices.value, filteredDevices.value, "id")
    : differenceBy(selectedDevices.value, filteredDevices.value, "id");
};

// The Standard column is future-proofing: hide it while the whole catalog is one standard.
const showStandardColumn = computed(
  () => new Set(rawDevices.value.map((d) => d.standard)).size > 1
);

const countryDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });
const countryName = (code: string): string => {
  if (!code || code === "XX") return code || "-";
  try {
    return countryDisplayNames.of(code) ?? code;
  } catch {
    return code;
  }
};

// Statements data
const statementsLoading = ref(false);
const statements = ref<CancellationStatement[]>([]);

// Filter options (from API)
const standardFilterOptions = ref<FilterOption[]>([]);
const statusFilterOptions = ref<FilterOption[]>([]);
const countryFilterOptions = ref<{ label: string; value: string }[]>([]);
const typeFilterOptions = ref<{ label: string; value: string }[]>([]);
const registryFilterOptions = ref<{ label: string; value: string }[]>([]);
const technologyCodeFilterOptions = ref<{ label: string; value: string }[]>([]);
const codFilterOptions = ref<{ label: string; value: string }[]>([]);

// Column visibility
const visibleColumns = useLocalStorage<EcolabelColumnKey[]>(
  "ecolabel-table-columns",
  DEFAULT_ECOLABEL_COLUMNS
);

const columnOptions = computed(() =>
  Object.values(ALL_ECOLABEL_COLUMNS).map((col) => ({
    label: t(col.header),
    value: col.field,
  }))
);

const isColVisible = (field: EcolabelColumnKey) =>
  visibleColumns.value.includes(field);

const getColWidth = (field: EcolabelColumnKey) =>
  ALL_ECOLABEL_COLUMNS[field].width.default;

// PrimeVue table filters
const tableFilters = ref({
  address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  plant_ecolabel_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  latitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
  longitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
  standard: { value: null, matchMode: FilterMatchMode.IN },
  status: { value: null, matchMode: FilterMatchMode.IN },
  country: { value: null, matchMode: FilterMatchMode.IN },
  renewable_type: { value: null, matchMode: FilterMatchMode.IN },
  capacity: { value: null, matchMode: FilterMatchMode.CONTAINS },
  cod: { value: null, matchMode: FilterMatchMode.IN },
  registration_authority: { value: null, matchMode: FilterMatchMode.IN },
  technology_code: { value: null, matchMode: FilterMatchMode.IN },
});

// Ranked name search; typo-tolerant so a partially wrong or partial name still surfaces
// the right device at the top. Other columns filter themselves via tableFilters.
// PERF: the input deliberately does NOT write into `tableFilters` — PrimeVue deep-watches
// the filter model and re-runs its whole pipeline on every keystroke. Instead the search
// lives in its own ref and is debounced before the (catalog-wide) ranking pass runs.
const searchInput = ref<string | null>(null);

const debouncedSearchTerm = refDebounced(searchInput, 250);

const displayedDevices = computed(() => {
  const query = debouncedSearchTerm.value;
  if (!query || !query.trim()) return devices.value;
  return searchEcolabelDevices(devices.value, query);
});

// The rows the table actually shows: the ranked name search intersected with the active
// column filters, using the same PrimeVue match functions the table itself runs. Derived
// here rather than from @value-change because PrimeVue does not re-emit when the :value
// prop changes, which would let select-all and CSV export drift from the visible set.
// PERF: also backs the controlled select-all — PrimeVue's built-in header checkbox
// recomputes "are all rows selected" as every×some over the whole selection (O(n²));
// :selectAll + @select-all-change answer the same question with an id Set in O(n).
const hasActiveFilterValue = (filter: { value: unknown }) =>
  filter.value !== null &&
  filter.value !== "" &&
  !(Array.isArray(filter.value) && filter.value.length === 0);

// Whether anything narrows the view at all — drives the "showing X of Y" footer even
// when the narrowed result happens to match every device.
const anyFilterActive = computed(
  () =>
    !!debouncedSearchTerm.value?.trim() ||
    Object.values(tableFilters.value).some(hasActiveFilterValue)
);

const filteredDevices = computed(() => {
  const entries = Object.entries(tableFilters.value) as [
    string,
    { value: unknown; matchMode: string },
  ][];
  const activeFilters = entries.filter(([, filter]) =>
    hasActiveFilterValue(filter)
  );
  if (activeFilters.length === 0) return displayedDevices.value;
  const { filters: matchers } = FilterService as unknown as {
    filters: Record<string, (value: unknown, filter: unknown) => boolean>;
  };
  return displayedDevices.value.filter((device) =>
    activeFilters.every(([field, filter]) =>
      matchers[filter.matchMode](
        (device as unknown as Record<string, unknown>)[field],
        filter.value
      )
    )
  );
});

// WORKAROUND: PrimeVue's virtual scroller keeps its old scroll offset when the result set
// shrinks, which can leave the viewport past the end of the list (blank table) — jump
// back to the top when the search or a column filter changes the row set. Deliberately
// keyed to the filter inputs, not the data: a background refresh replacing the same rows
// must not move the user's viewport.
watch(
  [debouncedSearchTerm, tableFilters],
  async () => {
    await nextTick();
    dt.value?.$el?.querySelector(".p-virtualscroller")?.scrollTo({ top: 0 });
  },
  { deep: true }
);

// Modals
const showEditModal = ref(false);
const showUploadWizard = ref(false);
const showManageDevices = ref(false);
const editingDevice = ref<EcolabelDevice | null>(null);
const managingStatement = ref<CancellationStatement | null>(null);

// Formatting
const formatStandard = (standard: string) => standard.replace(/_/g, " ");
const formatStatus = (status: string) => status.replace(/_/g, " ");

const getStandardBadgeClass = (standard: string) => {
  const classes: Record<string, string> = {
    EKOENERGY: "badge bg-success",
    OK_POWER: "badge bg-primary",
    OK_POWER_NEW: "badge bg-info",
    NATUREMADE_STAR: "badge bg-warning text-dark",
    GREEN_E: "badge bg-secondary",
    OTHER: "badge bg-light text-dark",
  };
  return classes[standard] ?? "badge bg-secondary";
};

const getStatusBadgeClass = (status: string) => {
  const classes: Record<string, string> = {
    APPROVED: "badge bg-success",
    PRE_APPROVED: "badge bg-success",
    PENDING_REVIEW: "badge bg-warning text-dark",
    NOT_APPROVED: "badge bg-secondary",
    REJECTED: "badge bg-danger",
  };
  return classes[status] ?? "badge bg-secondary";
};

// Guards against overlapping refreshes (two quick saves/deletes): only the newest
// request may apply its result, so a slow earlier response can't restore stale rows.
let devicesRequestGeneration = 0;

// Data loading. Background refetches (e.g. keeping the catalog in sync after an
// approval in the submissions tab) skip the loading flag: the table stays mounted
// here, but the PrimeVue loading overlay would still blank the rows the user is
// looking at mid-review.
const loadDevices = async (options: { background?: boolean } = {}) => {
  const generation = ++devicesRequestGeneration;
  if (!options.background) loading.value = true;
  try {
    const pageSize = 1000;
    let offset = 0;
    let total = 0;
    const fetchedDevices: EcolabelDevice[] = [];

    // Fetch all devices in pages. API caps per-request limit to 1000.
    do {
      const response = await ecolabelsApi.listDevices({}, offset, pageSize);
      fetchedDevices.push(...response.data.devices);
      total = response.data.total;
      offset += response.data.devices.length;
      if (response.data.devices.length === 0) break;
    } while (offset < total);

    if (generation !== devicesRequestGeneration) return;
    rawDevices.value = fetchedDevices;
    devices.value = fetchedDevices.map((d) => ({
      ...d,
      document_count: 0,
    }));
    selectedDevices.value = [];
    buildDerivedFilterOptions();
    if (fetchedDevices.length > 0) {
      const ids = fetchedDevices.map((d) => d.id);
      try {
        const countsRes = await ecolabelsApi.getDocumentCounts(ids);
        const counts = countsRes.data.counts;
        if (generation !== devicesRequestGeneration) return;
        devices.value.forEach((d) => {
          d.document_count = counts[d.id] ?? 0;
        });
      } catch {
        // Non-critical: don't fail device loading if counts fail
      }
    }
  } catch (error) {
    // A superseded request's failure is irrelevant — its result would be discarded.
    if (generation === devicesRequestGeneration) handleLoadError(error);
  } finally {
    // The latest request owns the spinner: a superseded foreground load leaves it for
    // its successor — even a background one — to clear once fresh data has landed.
    if (generation === devicesRequestGeneration) loading.value = false;
  }
};

const loadFilterOptions = async () => {
  try {
    const [standardsRes, statusesRes, countriesRes, typesRes] =
      await Promise.all([
        ecolabelsApi.getStandards(),
        ecolabelsApi.getStatuses(),
        ecolabelsApi.getCountries(),
        ecolabelsApi.getRenewableTypes(),
      ]);
    standardFilterOptions.value = standardsRes.data.standards;
    statusFilterOptions.value = statusesRes.data.statuses;
    countryFilterOptions.value = countriesRes.data.countries
      .map((c) => ({ label: countryName(c), value: c }))
      .sort((a, b) => a.label.localeCompare(b.label));
    typeFilterOptions.value = typesRes.data.renewable_types.map((t) => ({
      label: t,
      value: t,
    }));
  } catch (error) {
    handleLoadError(error);
  }
};

const formatCod = (cod: string | null) => {
  if (!cod) return "-";
  return formatDate(cod);
};

const buildDerivedFilterOptions = () => {
  const registries = new Set<string>();
  const techCodes = new Set<string>();
  const codValues = new Set<string>();
  for (const d of devices.value) {
    if (d.registration_authority) registries.add(d.registration_authority);
    if (d.technology_code) techCodes.add(d.technology_code);
    if (d.cod) codValues.add(d.cod);
  }
  registryFilterOptions.value = [...registries]
    .sort()
    .map((r) => ({ label: r, value: r }));
  technologyCodeFilterOptions.value = [...techCodes]
    .sort()
    .map((t) => ({ label: t, value: t }));
  codFilterOptions.value = [...codValues]
    .sort()
    .map((c) => ({ label: formatDate(c), value: c }));
};

// Actions
const openEditModal = (device: EcolabelDevice) => {
  editingDevice.value = device;
  showEditModal.value = true;
};
const closeEditModal = () => {
  showEditModal.value = false;
  editingDevice.value = null;
};
const onDeviceSaved = () => {
  closeEditModal();
  loadDevices({ background: true });
  globalSuccessHandler(t("ecolabels.device_saved"), "");
};

const openUploadWizard = () => {
  showUploadWizard.value = true;
};
const closeUploadWizard = () => {
  showUploadWizard.value = false;
};
const onStatementUploaded = () => {
  closeUploadWizard();
  loadStatements();
  globalSuccessHandler(t("ecolabels.certificates.statement_uploaded"), "");
};

const openManageDevices = (stmt: CancellationStatement) => {
  managingStatement.value = stmt;
  showManageDevices.value = true;
};
const closeManageDevices = () => {
  showManageDevices.value = false;
  managingStatement.value = null;
};
const onManageDevicesSaved = () => {
  closeManageDevices();
  loadStatements();
};

const loadStatements = async () => {
  statementsLoading.value = true;
  try {
    const response = await ecolabelsApi.listStatements(0, 1000);
    statements.value = response.data.statements;
  } catch (error) {
    handleLoadError(error);
  } finally {
    statementsLoading.value = false;
  }
};

const confirmDelete = async (device: EcolabelDevice) => {
  if (!confirm(t("ecolabels.confirm_delete", { name: device.name }))) return;
  try {
    await ecolabelsApi.deleteDevice(device.id);
    loadDevices({ background: true });
    globalSuccessHandler(t("ecolabels.device_deleted"), "");
  } catch (error) {
    globalErrorHandler(error);
  }
};

const confirmBulkDelete = async () => {
  const ids = selectedDevices.value.map((d) => d.id);
  if (!confirm(t("ecolabels.confirm_bulk_delete", { count: ids.length })))
    return;
  try {
    const response = await ecolabelsApi.bulkDeleteDevices(ids);
    loadDevices({ background: true });
    globalSuccessHandler(
      t("ecolabels.bulk_deleted", { count: response.data.deleted_count }),
      ""
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

const exportCSV = async () => {
  try {
    // Export exactly the rows the table shows: the ranked fuzzy search and column
    // filters run client-side, so send the visible ids rather than re-deriving an
    // (inevitably different) filter set server-side. When nothing narrows the view,
    // omit the ids entirely — a full export must not hit the request's id cap as the
    // catalog grows.
    const exportingSubset = filteredDevices.value.length < devices.value.length;
    const response = await ecolabelsApi.exportDevices(
      exportingSubset
        ? { device_ids: filteredDevices.value.map((d) => d.id) }
        : {}
    );
    const filename = `ecolabel-devices-${new Date().toISOString().split("T")[0]}.csv`;
    downloadFromApiResponse(
      response.data,
      response.headers,
      filename,
      "text/csv"
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

// Device documents modal: the original upload documents behind a device, ACL-scoped
// server-side (sellers only see their own companies' uploads).
type DeviceDocument = {
  id: number;
  company_id: number;
  file_name: string;
  upload_source: string;
  created_at: string;
};
const documentsModalDevice = ref<EcolabelDevice | null>(null);
const deviceDocuments = ref<DeviceDocument[]>([]);
const documentsLoading = ref(false);

const openDocumentsModal = async (device: EcolabelDevice) => {
  documentsModalDevice.value = device;
  documentsLoading.value = true;
  deviceDocuments.value = [];
  try {
    const response = await ecolabelsApi.getDeviceDocuments(device.id);
    // Guard against a slower earlier request landing after the user switched devices.
    if (documentsModalDevice.value?.id === device.id) {
      deviceDocuments.value = response.data.documents;
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    if (documentsModalDevice.value?.id === device.id) {
      documentsLoading.value = false;
    }
  }
};

const downloadDocument = async (doc: DeviceDocument) => {
  try {
    const response = await ecolabelsApi.downloadDocument(
      doc.company_id,
      doc.id
    );
    downloadFromApiResponse(
      response.data,
      response.headers,
      doc.file_name,
      "application/octet-stream"
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

// Deep link from the upload flow's duplicate hints: /ecolabels?device-name=<name>.
// Reactive, not mount-only: opening a second duplicate hint while the page is already
// mounted must update the filter too.
watch(
  () => route.query["device-name"],
  (nameParam) => {
    if (typeof nameParam === "string" && nameParam.trim()) {
      // Same cap as the input's maxlength — a script-assigned value bypasses that attribute.
      searchInput.value = clampQuery(nameParam);
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await Promise.all([loadFilterOptions(), loadDevices(), loadStatements()]);
});
</script>

<style scoped lang="scss">
.ecolabels-content {
  background-color: var(--phoenix-body-bg);
  border-radius: 1rem 1rem 0 0;
  overflow-y: auto;
  height: 100%;
  min-height: 0;
  overscroll-behavior: contain;
}

.column-selector {
  max-width: 200px;
}

:deep(.ecolabel-datatable) {
  .p-datatable-thead > tr > th {
    background-color: var(--phoenix-body-bg);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--phoenix-body-color);
    border-bottom: 1px solid var(--phoenix-border-color);
  }

  .p-datatable-tbody > tr {
    &:hover {
      background-color: rgba(var(--phoenix-primary-rgb), 0.04);
    }

    &.p-highlight {
      background-color: rgba(var(--phoenix-primary-rgb), 0.08);
    }

    > td {
      border-bottom: 1px solid var(--phoenix-border-color);
      font-size: 0.875rem;
    }
  }

  .p-column-filter {
    font-size: 0.8rem;
  }

  // PrimeVue computes each frozen column's sticky offset inline; unstyled mode leaves
  // the sticky positioning itself to us. Solid backgrounds so scrolled columns
  // disappear underneath instead of shining through.
  // Unstyled mode drops PrimeVue's theme classes, but the data attribute and the
  // inline left-offset survive — so the attribute is the hook.
  [data-p-frozen-column="true"] {
    position: sticky;
    z-index: 1;
    background-color: var(--phoenix-body-bg);
  }

  .p-datatable-thead > tr > th[data-p-frozen-column="true"] {
    z-index: 2;
  }
}

.pv-datatable-sort-icon {
  font-size: 0.7rem;
  margin-left: 0.25rem;
  opacity: 0.6;
}
</style>
